import { CurrencyPipe } from '@angular/common';
import { ChangeDetectorRef, Component, inject, OnInit } from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { Router } from '@angular/router';

import { asapScheduler, BehaviorSubject, combineLatest, debounceTime } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import type {
  Addon,
  BillingEstimate,
  BillingPeriod,
  BillingPlan,
  PaymentInterval,
} from '@core/billing-types';
import { AddonType, BillingPlanTier } from '@core/billing-types';
import {
  formDetectChanges,
  URL_ACTION_TRIGGER_BASE_PATH,
  UrlActionTrigger,
} from '@core/common-legacy';
import { InfoboxComponent } from '@core/components-infobox-legacy';
import { I18nService, TranslatePipe } from '@core/i18n-legacy';
import { DateFormatTimezonePipe } from '@core/time-legacy';
import type { DialogComponentI } from '@core/user-interface-legacy';
import {
  ButtonComponent,
  ButtonLoadingDirective,
  CheckboxComponent,
  DialogBodyComponent,
  DialogFooterComponent,
  DialogHeaderComponent,
  DialogRef,
  DividerComponent,
  HeadingComponent,
  MessageComponent,
  SpinnerComponent,
} from '@core/user-interface-legacy';
import {
  BillingEstimateDataService,
  BillingPlansDataService,
  SubscriptionDataService,
} from '@recruitee/billing-logic';

import { calculateSlotsLimitExceeded } from '../../helpers/calculate-slots-limit';
import { getActiveProductOrThrow } from '../../helpers/get-active-product-or-throw';
import { getErrorMessage } from '../../helpers/get-error-message';
import { getPaymentIntervalText } from '../../helpers/get-payment-interval-text';
import { getProductCost } from '../../helpers/get-product-cost';
import { getProductSafeSlotsCount } from '../../helpers/get-product-safe-slots-count';
import { ready } from '../../helpers/rx-operators';
import type { PricingFeatureLoadedModel } from '../../types/pricing-feature.types';
import {
  PricingFeatureFacade,
  PricingFeatureObservableModel,
} from '../../types/pricing-feature.types';
import { JobSlotsLimitMessageComponent } from '../job-slots-limit-message/job-slots-limit-message.component';
import { JobSlotsPickerComponent } from '../job-slots-picker/job-slots-picker.component';
import { SubscriptionCostComponent } from '../subscription-cost/subscription-cost.component';

export interface JobSlotsDialogModel {
  model: PricingFeatureLoadedModel;
}

@Component({
  selector: 'rt-job-slots-dialog',
  templateUrl: './job-slots-dialog.component.html',
  styleUrls: ['./job-slots-dialog.component.less'],
  imports: [
    DialogHeaderComponent,
    DialogBodyComponent,
    HeadingComponent,
    DividerComponent,
    JobSlotsPickerComponent,
    SubscriptionCostComponent,
    JobSlotsLimitMessageComponent,
    InfoboxComponent,
    CheckboxComponent,
    ReactiveFormsModule,
    SpinnerComponent,
    MessageComponent,
    DialogFooterComponent,
    ButtonComponent,
    ButtonLoadingDirective,
    CurrencyPipe,
    TranslatePipe,
    DateFormatTimezonePipe,
  ],
})
export class JobSlotsDialogComponent
  implements OnInit, JobSlotsDialogModel, DialogComponentI<JobSlotsDialogModel, any>
{
  private readonly dialogRef = inject(DialogRef);
  private readonly cdRef = inject(ChangeDetectorRef);
  private readonly facade = inject(PricingFeatureFacade);
  private readonly model$ = inject(PricingFeatureObservableModel);
  private readonly i18nService = inject(I18nService);
  private readonly router = inject(Router);

  private readonly billingPlansData = inject(BillingPlansDataService);
  private readonly subscriptionData = inject(SubscriptionDataService);
  private readonly estimateData = inject(BillingEstimateDataService);
  public activeProduct: BillingPlan;
  public slots: number;
  public ownedSlots: number;
  public usedSlots: { jobs: number };
  public slotsMinLimit: { jobs: number } & { exceeded: boolean };
  public model: PricingFeatureLoadedModel;
  public addons: Addon[];

  public paymentInterval: PaymentInterval;
  public paymentIntervalText: string;
  public errorMessage?: string;
  public consentControl = new FormControl<boolean>(false);
  public consentRequired: boolean;
  public billingEstimatePending = false;

  public selectedSlots: number | null;
  public selectedSlots$ = new BehaviorSubject<number | null>(null);
  public loading: boolean;
  public showFullTerms: boolean;
  public totalCostSelected: number;
  public billingEstimate: BillingEstimate;
  public legacyPlansInfoboxContent = this.getLegacyPlansInfoboxContent();

  public BillingPlanTier = BillingPlanTier;

  constructor() {
    this.cdRef.detach();
    this.facade.fetchPlansIfNeeded();
  }

  public ngOnInit(): void {
    combineLatest([
      this.model$.pipe(ready),
      this.billingPlansData.collection$,
      this.billingPlansData.activePlan$,
      this.subscriptionData.data$,
      this.selectedSlots$,
    ])
      .pipe(debounceTime(0, asapScheduler), takeUntil(this.dialogRef.afterClosed))
      .subscribe(([model, billingPlans, activePlan, { addons }]) => {
        this.onModelUpdate(model, billingPlans, activePlan);
        this.addons = addons;
        this.cdRef.detectChanges();
      });

    this.estimateData.data$
      .pipe(takeUntil(this.dialogRef.afterClosed))
      .subscribe(billingEstimate => {
        this.billingEstimate = billingEstimate;
        this.cdRef.detectChanges();
      });
    this.estimateData.isPending$
      .pipe(takeUntil(this.dialogRef.afterClosed))
      .subscribe(isPending => {
        this.billingEstimatePending = isPending;
        this.cdRef.detectChanges();
      });

    formDetectChanges(this.consentControl, this.cdRef, this.dialogRef.afterClosed);
  }

  // eslint-disable-next-line @typescript-eslint/no-empty-function
  public setDialogData(): void {}

  public slotsChanged(slots: number) {
    this.selectedSlots = slots;
    this.selectedSlots$.next(slots);
    this.updateSlotLimits();

    this.fetchEstimate(this.activeProduct.id, this.activeProduct.billingPeriod, slots);

    this.cdRef.detectChanges();
  }

  public close() {
    this.dialogRef.close();
  }

  public changeSlots(slots: number, currentSlots: number) {
    this.errorMessage = undefined;
    this.loading = true;
    this.cdRef.detectChanges();
    const { autoCollection, manual } = this.model.subscription ?? {};
    const isWithPaymentIntent = !manual || autoCollection;
    const textingAddon = this.addons.find(addon => addon.type === AddonType.Texting);
    const addons = [
      { type: AddonType.Slots, quantity: slots },
      ...(textingAddon ? [textingAddon] : []),
    ];
    this.facade
      .newChangeSlots(
        (<BillingPlan>this.activeProduct).id,
        this.paymentInterval,
        currentSlots < slots,
        addons,
        !!isWithPaymentIntent,
      )
      .subscribe({
        next: () => {
          this.dialogRef.close();
        },
        error: error => {
          this.errorMessage = getErrorMessage(error);
          this.loading = false;
          this.cdRef.detectChanges();
        },
      });
  }

  public onShowMore() {
    this.showFullTerms = true;
    this.cdRef.detectChanges();
  }

  private updateSlotLimits(): void {
    if (this.selectedSlots === null) return;

    this.slotsMinLimit = calculateSlotsLimitExceeded(this.selectedSlots, this.usedSlots.jobs);
  }

  private onModelUpdate(
    model: PricingFeatureLoadedModel,
    billingPlans: BillingPlan[],
    activeBillingPlan: BillingPlan | undefined,
  ) {
    this.model = model;
    this.activeProduct = getActiveProductOrThrow(model, billingPlans, activeBillingPlan);
    this.slots = model.slotsTotal;
    this.ownedSlots = Number.isFinite(model.slotsTotal) ? model.slotsTotal : model.jobSlotsUsed;

    this.selectedSlots = this.selectedSlots ?? getProductSafeSlotsCount(this.activeProduct, model);
    this.usedSlots = {
      jobs: model.jobSlotsUsed,
    };

    this.paymentInterval = model.subscription?.billingPeriod || 12;
    this.paymentIntervalText = getPaymentIntervalText(this.paymentInterval, this.i18nService);
    this.totalCostSelected = getProductCost(
      this.activeProduct,
      this.selectedSlots,
      this.paymentInterval,
      this.addons,
    );
    this.consentRequired = !!model.subscription?.manual;
    this.updateSlotLimits();
  }

  private fetchEstimate(billingPlanId: number, billingPeriod: BillingPeriod, slots: number) {
    const textingAddon = this.addons.find(addon => addon.type === AddonType.Texting);
    const addons = [
      { type: AddonType.Slots, quantity: slots },
      ...(textingAddon ? [textingAddon] : []),
    ];
    this.estimateData.fetch({
      billingPlanId,
      billingPeriod,
      addons,
    });
  }

  private getLegacyPlansInfoboxContent() {
    const intercomTree = this.router.createUrlTree(
      [`/${URL_ACTION_TRIGGER_BASE_PATH}`, UrlActionTrigger.IntercomOpen],
      { queryParamsHandling: 'preserve' },
    );
    return this.i18nService.translate('app.pricing.legacy_plans_phase_out_infobox.content', {
      plan_picker_link: this.router.serializeUrl(this.facade.getPlanPickerUrl()),
      intercom_link: this.router.serializeUrl(intercomTree),
    });
  }
}
